/**
 * API Client
 *
 * Handles communication with the bug reporter backend API.
 * Submits bug reports with screenshots, metadata, and Turnstile tokens.
 *
 * @module api-client
 */

import type { SubmitBugReportResponse, WidgetConfig, ClientAttachment } from '@wds/bug-reporter-types';

/**
 * Data required for submitting a bug report
 */
export interface SubmitBugReportData {
  /** Report title (5-200 characters) */
  title: string;

  /** Bug description (10-5000 characters) */
  description: string;

  /** Reporter email (optional - required-ness is decided per-client by widget config) */
  email?: string;

  /** Screenshot as base64-encoded data URL (optional - e.g., "data:image/png;base64,...") */
  screenshot?: string | null;

  /** Attachments array (replaces single screenshot for multi-attachment support) */
  attachments?: ClientAttachment[];

  /** Browser information string (e.g., "Chrome 120.0.0") */
  browserInfo: string;

  /** Viewport size string (e.g., "1920x1080") */
  viewportSize: string;

  /** Current page URL */
  pageUrl: string;

  /** Full user agent string */
  userAgent: string;

  /** Cloudflare Turnstile token */
  turnstileToken: string;

  /** Honeypot field - should always be empty for real users */
  website: string;
}

/**
 * API error codes returned by the backend
 */
export enum BugReporterErrorCode {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_API_KEY = 'INVALID_API_KEY',
  ORIGIN_NOT_ALLOWED = 'ORIGIN_NOT_ALLOWED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  CAPTCHA_FAILED = 'CAPTCHA_FAILED',
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  ATTACHMENT_UPLOAD_FAILED = 'ATTACHMENT_UPLOAD_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Typed error for API client operations
 */
export class BugReporterError extends Error {
  constructor(
    message: string,
    public code: BugReporterErrorCode,
    public statusCode?: number,
    public details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'BugReporterError';
  }
}

/**
 * Convert base64 data URL to Blob
 *
 * @param dataUrl - Base64 data URL (e.g., "data:image/png;base64,...")
 * @returns Blob containing the image data
 *
 * @internal
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const base64 = parts[1];

  // Decode base64
  const byteString = atob(base64);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mime });
}

/**
 * Get file extension from MIME type
 *
 * @param mimeType - MIME type string (e.g., "image/png")
 * @returns File extension with dot (e.g., ".png")
 *
 * @internal
 */
function getFileExtension(mimeType: string): string {
  const mimeToExt: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'application/pdf': '.pdf',
    'text/plain': '.txt',
    'application/json': '.json',
  };

  return mimeToExt[mimeType] || '.bin';
}

/**
 * Submit a bug report to the backend API
 *
 * Sends a POST request to `/api/bug-reporter/reports` with multipart/form-data.
 * Includes API key authentication, screenshot upload, and Turnstile token.
 *
 * @param apiBaseUrl - Base URL for the API (e.g., "https://api.example.com")
 * @param apiKey - API key for authentication
 * @param data - Bug report data
 * @returns Promise resolving to submission response
 * @throws {BugReporterError} If submission fails
 *
 * @example
 * ```typescript
 * try {
 *   const response = await submitBugReport(
 *     'https://api.example.com',
 *     'bw_live_abc123...',
 *     {
 *       title: 'Login button broken',
 *       description: 'The login button does not respond...',
 *       screenshot: 'data:image/png;base64,...',
 *       browserInfo: 'Chrome 120.0.0',
 *       viewportSize: '1920x1080',
 *       pageUrl: 'https://example.com/login',
 *       userAgent: 'Mozilla/5.0...',
 *       turnstileToken: 'token123...'
 *     }
 *   );
 *
 *   console.log('Report submitted:', response.reportId);
 * } catch (error) {
 *   if (error instanceof BugReporterError) {
 *     console.error(`Error [${error.code}]:`, error.message);
 *   }
 * }
 * ```
 */
export async function submitBugReport(
  apiBaseUrl: string,
  apiKey: string,
  data: SubmitBugReportData
): Promise<SubmitBugReportResponse> {
  try {
    // Construct endpoint URL
    const url = `${apiBaseUrl.replace(/\/$/, '')}/api/bug-reporter/reports`;

    // Create FormData
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('description', data.description);
    // Reporter email — only sent when provided. Server enforces required-ness
    // per-client via widget config (requiredFields). Omitting an empty value
    // keeps the payload clean and lets the optional server schema pass.
    if (data.email && data.email.trim().length > 0) {
      formData.append('email', data.email.trim());
    }
    formData.append('pageUrl', data.pageUrl);
    formData.append('browserInfo', data.browserInfo);
    formData.append('viewportSize', data.viewportSize);
    formData.append('userAgent', data.userAgent);
    formData.append('turnstileToken', data.turnstileToken);
    formData.append('website', data.website); // Honeypot field

    // Add screenshot if provided (legacy support, optional)
    if (data.screenshot) {
      const screenshotBlob = dataUrlToBlob(data.screenshot);
      formData.append('screenshot', screenshotBlob, 'screenshot.png');
    }

    // Add attachments array if provided (new multi-attachment support)
    if (data.attachments && data.attachments.length > 0) {
      // Validate attachments before processing
      for (let i = 0; i < data.attachments.length; i++) {
        const attachment = data.attachments[i];

        // Validate file object
        if (!attachment.file || !(attachment.file instanceof Blob)) {
          throw new BugReporterError(
            `Attachment at index ${i} is invalid: file is missing or not a valid file object`,
            BugReporterErrorCode.ATTACHMENT_UPLOAD_FAILED,
            400
          );
        }

        // Validate file size (10MB limit per attachment)
        const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB in bytes
        if (attachment.file.size > MAX_ATTACHMENT_SIZE) {
          throw new BugReporterError(
            `Attachment "${attachment.originalName || `attachment-${i}`}" exceeds maximum size of 10MB`,
            BugReporterErrorCode.FILE_TOO_LARGE,
            413
          );
        }

        // Validate file type if available
        if (attachment.file.type && !attachment.file.type.startsWith('image/') && !attachment.file.type.startsWith('application/')) {
          throw new BugReporterError(
            `Attachment "${attachment.originalName || `attachment-${i}`}" has unsupported file type: ${attachment.file.type}`,
            BugReporterErrorCode.ATTACHMENT_UPLOAD_FAILED,
            400
          );
        }

        try {
          // Append file with original filename or generated name
          const fileName = attachment.originalName || `${attachment.type}-${i}${getFileExtension(attachment.file.type)}`;
          formData.append('attachments', attachment.file, fileName);

          // Append attachment metadata as separate fields
          formData.append(`attachment_type_${i}`, attachment.type);
          if (attachment.originalName) {
            formData.append(`attachment_originalName_${i}`, attachment.originalName);
          }
        } catch (appendError) {
          throw new BugReporterError(
            `Failed to process attachment "${attachment.originalName || `attachment-${i}`}": ${appendError instanceof Error ? appendError.message : 'Unknown error'}`,
            BugReporterErrorCode.ATTACHMENT_UPLOAD_FAILED,
            400
          );
        }
      }
    }

    // Debug: Log FormData contents before sending
    console.log('[api-client] Sending request to:', url);
    console.log('[api-client] FormData contents:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof Blob) {
        console.log(`  ${key}: [Blob] size=${value.size}, type=${value.type}`);
      } else {
        console.log(`  ${key}: ${typeof value === 'string' ? value.substring(0, 100) : value}`);
      }
    }

    // Send request
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-API-Key': apiKey,
      },
      body: formData,
      // Note: credentials: 'include' removed - API key auth via X-API-Key header, no cookies needed
    });

    // Parse response
    const responseData = await response.json();

    // Handle errors
    if (!response.ok) {
      const statusCode = response.status;

      // Map HTTP status to error code
      let errorCode: BugReporterErrorCode;
      switch (statusCode) {
        case 400:
          errorCode = responseData.code === 'CAPTCHA_FAILED'
            ? BugReporterErrorCode.CAPTCHA_FAILED
            : BugReporterErrorCode.VALIDATION_ERROR;
          break;
        case 401:
          errorCode = BugReporterErrorCode.INVALID_API_KEY;
          break;
        case 403:
          errorCode = BugReporterErrorCode.ORIGIN_NOT_ALLOWED;
          break;
        case 413:
          errorCode = BugReporterErrorCode.FILE_TOO_LARGE;
          break;
        case 429:
          errorCode = BugReporterErrorCode.RATE_LIMIT_EXCEEDED;
          break;
        default:
          errorCode = BugReporterErrorCode.UNKNOWN_ERROR;
      }

      // Log full error response for debugging
      console.error('[api-client] Request failed:', {
        statusCode,
        error: responseData.error,
        details: responseData.details,
        fullResponse: responseData,
      });

      throw new BugReporterError(
        responseData.error || 'Failed to submit bug report',
        errorCode,
        statusCode,
        responseData.details
      );
    }

    return responseData as SubmitBugReportResponse;
  } catch (error) {
    // Re-throw BugReporterError as-is
    if (error instanceof BugReporterError) {
      throw error;
    }

    // Network errors (fetch failed)
    if (error instanceof TypeError) {
      throw new BugReporterError(
        'Network error: Unable to connect to bug reporter API',
        BugReporterErrorCode.NETWORK_ERROR
      );
    }

    // Unknown errors
    throw new BugReporterError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      BugReporterErrorCode.UNKNOWN_ERROR
    );
  }
}

/**
 * Check if an error is a rate limit error
 *
 * @param error - Error to check
 * @returns True if error is a rate limit error
 */
export function isRateLimitError(error: unknown): boolean {
  return error instanceof BugReporterError && error.code === BugReporterErrorCode.RATE_LIMIT_EXCEEDED;
}

/**
 * Check if an error is a CAPTCHA error
 *
 * @param error - Error to check
 * @returns True if error is a CAPTCHA error
 */
export function isCaptchaError(error: unknown): boolean {
  return error instanceof BugReporterError && error.code === BugReporterErrorCode.CAPTCHA_FAILED;
}

/**
 * Check if an error is a network error
 *
 * @param error - Error to check
 * @returns True if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  return error instanceof BugReporterError && error.code === BugReporterErrorCode.NETWORK_ERROR;
}

/**
 * Check if an error is an attachment upload error
 *
 * @param error - Error to check
 * @returns True if error is an attachment upload error
 */
export function isAttachmentUploadError(error: unknown): boolean {
  return error instanceof BugReporterError && error.code === BugReporterErrorCode.ATTACHMENT_UPLOAD_FAILED;
}

/**
 * Get user-friendly error message for display
 *
 * @param error - Error to format
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (!(error instanceof BugReporterError)) {
    return 'An unexpected error occurred. Please try again.';
  }

  switch (error.code) {
    case BugReporterErrorCode.VALIDATION_ERROR:
      return 'Please check your input and try again.';
    case BugReporterErrorCode.INVALID_API_KEY:
      return 'Invalid API key. Please contact support.';
    case BugReporterErrorCode.ORIGIN_NOT_ALLOWED:
      return 'This domain is not authorized. Please contact support.';
    case BugReporterErrorCode.RATE_LIMIT_EXCEEDED:
      return 'Too many requests. Please try again later.';
    case BugReporterErrorCode.CAPTCHA_FAILED:
      return 'Verification failed. Please try again.';
    case BugReporterErrorCode.FILE_TOO_LARGE:
      return 'Screenshot file is too large. Please try again.';
    case BugReporterErrorCode.ATTACHMENT_UPLOAD_FAILED:
      return 'Failed to upload one or more attachments. Please check your files and try again.';
    case BugReporterErrorCode.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again.';
    default:
      return 'Failed to submit bug report. Please try again.';
  }
}

/**
 * Fetch widget configuration from the API
 *
 * Retrieves customization settings for the widget (brand color, logo, button text, etc.).
 * The endpoint is public and cached (5 minutes) for performance.
 *
 * @param apiBaseUrl - Base URL for the API (e.g., "https://api.example.com")
 * @param clientId - Client ID extracted from API key
 * @returns Promise resolving to widget configuration
 * @throws {Error} If fetch fails or response is invalid
 *
 * @example
 * ```typescript
 * try {
 *   const config = await fetchWidgetConfig(
 *     'https://api.example.com',
 *     'abc123'
 *   );
 *   console.log('Brand color:', config.brandColor);
 * } catch (error) {
 *   console.warn('Failed to fetch config, using defaults');
 * }
 * ```
 */
export async function fetchWidgetConfig(
  apiBaseUrl: string,
  clientId: string
): Promise<WidgetConfig> {
  try {
    // Construct endpoint URL
    const url = `${apiBaseUrl.replace(/\/$/, '')}/api/bug-reporter/clients/${clientId}/config`;

    // Send request
    const response = await fetch(url, {
      method: 'GET',
      // Note: credentials: 'include' removed - API key auth via X-API-Key header, no cookies needed
    });

    // Handle errors
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse and return response
    // API returns { success: true, data: {...config...} }
    const responseData = await response.json();
    return responseData.data as WidgetConfig;
  } catch (error) {
    // Re-throw with more context
    throw new Error(
      `Failed to fetch widget config: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Extract client ID from API key
 *
 * API key format: bw_live_<clientId>_<secret>
 * Example: bw_live_abc123_xyz789 → abc123
 *
 * @param apiKey - Full API key
 * @returns Client ID
 * @throws {Error} If API key format is invalid
 *
 * @example
 * ```typescript
 * const clientId = extractClientId('bw_live_abc123_xyz789');
 * // Result: 'abc123'
 * ```
 */
export function extractClientId(apiKey: string): string {
  // Expected format: bw_live_<clientId>_<secret>
  const parts = apiKey.split('_');

  // Validate format
  if (parts.length < 4 || parts[0] !== 'bw' || parts[1] !== 'live') {
    throw new Error('Invalid API key format. Expected: bw_live_<clientId>_<secret>');
  }

  // Client ID is the 3rd part (index 2)
  const clientId = parts[2];

  if (!clientId || clientId.length === 0) {
    throw new Error('Invalid API key: client ID is empty');
  }

  return clientId;
}
